// crewbit-admin/src/components/money/BulkActionConfirmModal.tsx
//
// §Money.HardeningBulkPass2 Phase 3 — generic confirmation + result modal for
// the 4 bulk invoice actions (delete-drafts / cancel / send-reminder / mark-paid).
//
// Per `feedback_anti_silent_operations.md` §T.83 refinement: every state-transition
// action ships confirmation modal + visible success/error feedback. This modal
// satisfies (a) confirmation + (b) result surfacing for the bulk path.
//
// Generic shape: caller provides action label + per-action wrapper function +
// selected invoices. Modal:
//   - Lists items (truncated if >10) so operator sees what they're about to do
//   - On confirm, invokes wrapper; on success → success toast w/ summary +
//     calls onResult (which closes modal + refreshes list)
//   - On per-invoice partial failure → warning toast w/ failed_count summary;
//     leaves modal open with failed-list rendered inline so operator sees what
//     didn't go through
//
// Action-specific reason input (cancel only) supplied by parent via `reasonInput`
// render-prop; mark-paid method dropdown supplied via `methodPicker` render-prop.

import { useState } from 'react';
import { palette, radii, spacing, typography } from '../../styles/tokens';
import type { MoneyRpcResult } from '../../lib/moneyRpc';
import type { BulkInvoiceEnvelope, BulkInvoiceFailure } from '../../lib/invoiceBulkMutations';
import type { InvoiceRow } from '../../types';

export type BulkActionKind = 'delete_drafts' | 'cancel' | 'send_reminder' | 'mark_paid';

export interface BulkActionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: BulkActionKind;
  selectedInvoices: InvoiceRow[];
  /** Wrapper that performs the bulk RPC call. Returns the result envelope. */
  perform: (invoiceIds: string[]) => Promise<MoneyRpcResult<BulkInvoiceEnvelope>>;
  /** Optional render-prop for cancel-action reason input. */
  reasonInput?: React.ReactNode;
  /** Optional render-prop for mark-paid method dropdown. */
  methodPicker?: React.ReactNode;
  /** Result handler — fires after successful bulk call (success_count > 0).
   *  Parent uses to close + refresh + toast. */
  onResult: (envelope: BulkInvoiceEnvelope) => void;
}

const TITLE_BY_ACTION: Record<BulkActionKind, (n: number) => string> = {
  delete_drafts: (n) => `Delete ${n} draft${n === 1 ? '' : 's'}?`,
  cancel: (n) => `Cancel ${n} invoice${n === 1 ? '' : 's'}?`,
  send_reminder: (n) => `Send reminder to ${n} invoice${n === 1 ? '' : 's'}?`,
  mark_paid: (n) => `Mark ${n} invoice${n === 1 ? '' : 's'} as paid?`,
};

const BODY_BY_ACTION: Record<BulkActionKind, (n: number) => string> = {
  delete_drafts: (n) => `${n} draft invoice${n === 1 ? '' : 's'} will be permanently deleted. This can't be undone.`,
  cancel: (n) => `${n} invoice${n === 1 ? '' : 's'} will be cancelled. Cancellation reverses payment-ledger entries and writes activity-log audit entries.`,
  send_reminder: (n) => `Email reminders will be sent to client${n === 1 ? '' : 's'} for ${n} invoice${n === 1 ? '' : 's'}.`,
  mark_paid: (n) => `${n} invoice${n === 1 ? '' : 's'} will be marked as paid manually. Payment-ledger entries will be created for each.`,
};

const CONFIRM_LABEL_BY_ACTION: Record<BulkActionKind, string> = {
  delete_drafts: 'Delete',
  cancel: 'Cancel invoices',
  send_reminder: 'Send reminders',
  mark_paid: 'Mark as paid',
};

const DESTRUCTIVE_ACTIONS: BulkActionKind[] = ['delete_drafts', 'cancel'];

const TRUNCATE_AFTER = 10;

export function BulkActionConfirmModal(props: BulkActionConfirmModalProps) {
  const { isOpen, onClose, action, selectedInvoices, perform, reasonInput, methodPicker, onResult } = props;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partialFailures, setPartialFailures] = useState<BulkInvoiceFailure[] | null>(null);

  if (!isOpen) return null;

  const n = selectedInvoices.length;
  const isDestructive = DESTRUCTIVE_ACTIONS.includes(action);
  const visibleInvoices = selectedInvoices.slice(0, TRUNCATE_AFTER);
  const hiddenCount = Math.max(0, n - TRUNCATE_AFTER);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    setPartialFailures(null);
    const result = await perform(selectedInvoices.map((i) => i.id));
    setSubmitting(false);
    if (!result.success) {
      setError(result.error.code === 'FORBIDDEN'
        ? 'You don\'t have permission for this bulk action.'
        : result.error.code === 'NO_ACTIVE_BUSINESS'
          ? 'No active business — log out and back in.'
          : `Bulk action failed (${result.error.code ?? 'UNKNOWN'}).`);
      return;
    }
    if (result.data.failed_count > 0) {
      setPartialFailures(result.data.failed);
    }
    onResult(result.data);
    if (result.data.failed_count === 0) {
      // Full success: close immediately. Partial failures stay open so operator
      // sees the failed list inline.
      onClose();
    }
  };

  const confirmDisabled = submitting || n === 0;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="bulk-action-title">
      <div style={cardStyle}>
        <h2 id="bulk-action-title" style={titleStyle}>
          {TITLE_BY_ACTION[action](n)}
        </h2>
        <p style={bodyStyle}>{BODY_BY_ACTION[action](n)}</p>

        {reasonInput ? <div style={{ marginBottom: spacing.md }}>{reasonInput}</div> : null}
        {methodPicker ? <div style={{ marginBottom: spacing.md }}>{methodPicker}</div> : null}

        <div style={listWrapStyle}>
          <div style={listLabelStyle}>Selected invoices:</div>
          <ul style={listStyle}>
            {visibleInvoices.map((inv) => (
              <li key={inv.id} style={listItemStyle}>
                <span style={{ fontFamily: 'inherit', fontWeight: typography.weight.semibold }}>
                  {inv.invoice_number}
                </span>
                <span style={{ color: palette.g500, marginLeft: spacing.sm }}>
                  {inv.client_name ?? '—'}
                </span>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <div style={{ ...listItemStyle, color: palette.g500 }}>
              + {hiddenCount} more
            </div>
          )}
        </div>

        {partialFailures && partialFailures.length > 0 && (
          <div style={partialFailureStyle}>
            <strong style={{ color: palette.amber }}>{partialFailures.length} failed</strong>
            <ul style={{ margin: `${spacing.xs}px 0 0 ${spacing.md}px`, padding: 0 }}>
              {partialFailures.slice(0, 5).map((f) => {
                const code = (f.error as { code?: string })?.code ?? 'UNKNOWN';
                return (
                  <li key={f.invoice_id} style={{ color: palette.g600, fontSize: typography.size.caption }}>
                    {f.invoice_id.slice(0, 8)}…  <code style={{ color: palette.amber }}>{code}</code>
                  </li>
                );
              })}
              {partialFailures.length > 5 && (
                <li style={{ color: palette.g500, fontSize: typography.size.caption }}>
                  + {partialFailures.length - 5} more
                </li>
              )}
            </ul>
          </div>
        )}

        {error && (
          <div style={errorStyle} role="alert">
            {error}
          </div>
        )}

        <div style={buttonRowStyle}>
          <button type="button" onClick={onClose} style={cancelButtonStyle} disabled={submitting}>
            {partialFailures ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            style={confirmButtonStyle(isDestructive, confirmDisabled)}
          >
            {submitting ? 'Working…' : CONFIRM_LABEL_BY_ACTION[action]}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const cardStyle: React.CSSProperties = {
  background: palette.white,
  borderRadius: radii.md,
  padding: spacing.xl,
  width: 'min(520px, 90vw)',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
};

const titleStyle: React.CSSProperties = {
  margin: `0 0 ${spacing.sm}px`,
  fontSize: typography.size.heading,
  fontWeight: typography.weight.semibold,
  color: palette.g800,
};

const bodyStyle: React.CSSProperties = {
  margin: `0 0 ${spacing.md}px`,
  fontSize: typography.size.body,
  color: palette.g600,
  lineHeight: 1.5,
};

const listWrapStyle: React.CSSProperties = {
  background: palette.g50,
  border: `1px solid ${palette.g150}`,
  borderRadius: radii.sm,
  padding: spacing.sm,
  marginBottom: spacing.md,
};

const listLabelStyle: React.CSSProperties = {
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  color: palette.g700,
  marginBottom: spacing.xs,
};

const listStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  maxHeight: 200,
  overflowY: 'auto',
};

const listItemStyle: React.CSSProperties = {
  padding: `4px 0`,
  fontSize: typography.size.caption,
};

const partialFailureStyle: React.CSSProperties = {
  background: palette.amberWash,
  border: `1px solid ${palette.amber}`,
  borderRadius: radii.sm,
  padding: spacing.sm,
  marginBottom: spacing.md,
};

const errorStyle: React.CSSProperties = {
  background: palette.redWash,
  border: `1px solid ${palette.red}`,
  borderRadius: radii.sm,
  padding: spacing.sm,
  marginBottom: spacing.md,
  color: palette.red,
  fontSize: typography.size.caption,
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: spacing.sm,
  marginTop: spacing.md,
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  color: palette.g700,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const confirmButtonStyle = (destructive: boolean, disabled: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  borderRadius: radii.sm,
  border: 'none',
  background: disabled ? palette.g200 : (destructive ? palette.red : palette.accent),
  color: disabled ? palette.g500 : palette.white,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
});
